import React from "react";
import { ContentArrayRenderer } from "./ContentArrayRenderer";
import { DatasetArrayRenderer } from "./DatasetArrayRenderer";
import { GenericArrayRenderer } from "./GenericArrayRenderer";

// Props interface for tool result renderers
export interface ToolResultRendererProps {
  data: unknown;
}

// Registry maps tool types/names to renderer components
const rendererRegistry: Record<
  string,
  React.ComponentType<ToolResultRendererProps>
> = {
  // Explicit tool type to renderer mappings
  list_datasets: DatasetArrayRenderer,
};

/**
 * Check if data is an array of objects with 'content' fields
 */
function isContentArray(data: unknown): data is Array<{ content: string }> {
  if (!Array.isArray(data) || data.length === 0) {
    console.log("[ARTIFACT_DEBUG] isContentArray failed: not array or empty", {
      isArray: Array.isArray(data),
      length: Array.isArray(data) ? data.length : "N/A",
    });
    return false;
  }
  // Check if at least the first item has a 'content' field
  // Content can be string or null (backend may return null for missing chunk data)
  const firstItem = data[0];
  const hasContent =
    typeof firstItem === "object" &&
    firstItem !== null &&
    "content" in firstItem;

  console.log("[ARTIFACT_DEBUG] isContentArray check:", {
    hasContent,
    firstItemType: typeof firstItem,
    contentValue: hasContent ? (firstItem as any).content : "N/A",
    contentType: hasContent ? typeof (firstItem as any).content : "N/A",
  });

  return hasContent;
}

/**
 * Check if data is an array of dataset objects (has name + dataset_type or dataset_type_id)
 */
function isDatasetArray(data: unknown): boolean {
  if (!Array.isArray(data) || data.length === 0) {
    return false;
  }
  const firstItem = data[0];
  if (typeof firstItem !== "object" || firstItem === null) {
    return false;
  }
  // Check for dataset-specific fields
  const hasName = "name" in firstItem;
  const hasDatasetType =
    "dataset_type" in firstItem || "dataset_type_id" in firstItem;
  const isDataset = hasName && hasDatasetType;

  console.log("[ARTIFACT_DEBUG] isDatasetArray check:", {
    isDataset,
    hasName,
    hasDatasetType,
  });

  return isDataset;
}

/**
 * Check if data is an array of objects (generic array that can be rendered nicely)
 */
function isGenericObjectArray(
  data: unknown,
): data is Array<Record<string, unknown>> {
  if (!Array.isArray(data) || data.length === 0) {
    return false;
  }
  // Check if at least the first item is an object
  const firstItem = data[0];
  const isObject =
    typeof firstItem === "object" &&
    firstItem !== null &&
    !Array.isArray(firstItem);

  console.log("[ARTIFACT_DEBUG] isGenericObjectArray check:", {
    isObject,
    firstItemType: typeof firstItem,
    isArray: Array.isArray(firstItem),
  });

  return isObject;
}

/**
 * Get the appropriate renderer for tool result data.
 *
 * Priority:
 * 1. Explicit type-based renderer from registry
 * 2. Pattern detection (arrays with content fields)
 * 3. Returns null to fall back to JSON viewer
 */
export function getToolResultRenderer(
  data: unknown,
  toolType?: string,
): React.ComponentType<ToolResultRendererProps> | null {
  console.log("[ARTIFACT_DEBUG] getToolResultRenderer:", {
    toolType,
    dataType: typeof data,
    data: data,
  });

  // 1. Check explicit registry by tool type
  if (toolType && rendererRegistry[toolType]) {
    console.log("[ARTIFACT_DEBUG] Found in registry:", toolType);
    return rendererRegistry[toolType];
  }

  // 2. Pattern detection: array with content fields (search results)
  if (isContentArray(data)) {
    console.log("[ARTIFACT_DEBUG] Matched ContentArrayRenderer");
    return ContentArrayRenderer;
  }

  // 3. Pattern detection: array of dataset objects
  if (isDatasetArray(data)) {
    console.log("[ARTIFACT_DEBUG] Matched DatasetArrayRenderer (by pattern)");
    return DatasetArrayRenderer;
  }

  // 4. Pattern detection: any array of objects (generic fallback)
  if (isGenericObjectArray(data)) {
    console.log("[ARTIFACT_DEBUG] Matched GenericArrayRenderer");
    return GenericArrayRenderer;
  }

  // 5. Return null to use JSON fallback
  console.log("[ARTIFACT_DEBUG] No renderer matched, using JSON fallback");
  return null;
}
